import { Injectable, effect, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { OrderService, orderNumber, type Order } from './order.service';
import { ToastService } from './toast.service';

/** How often an admin's open app asks for new orders. */
const POLL_MS = 45_000;
/** The newest order already seen, so each order alerts once (survives reloads). */
const SEEN_KEY = 'app-last-order-seen';
/** The baseline when there are no orders yet: sorts before any real timestamp. */
const NO_ORDERS = '0';
/** An alert stays up long enough to be noticed between glances at the screen. */
const TOAST_MS = 15_000;

/**
 * The live new-order alert: while an admin is signed in and the app is open it
 * checks the API for orders newer than the last one it saw, and for each batch
 * shows a toast and plays a short beep. Nothing runs for customers, and nothing
 * runs when the app is closed — email covers that case (see the API's
 * `notifications.py`).
 *
 * The first check on a device only records the newest order, so an admin is not
 * greeted with an alert for every order from before they signed in.
 */
@Injectable({ providedIn: 'root' })
export class OrderAlertService {
  private readonly auth = inject(AuthService);
  private readonly orders = inject(OrderService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private timer: ReturnType<typeof setInterval> | undefined;
  private checking = false;
  private audio: AudioContext | undefined;
  /** Kept in memory too, so alerts still work when storage is blocked. */
  private seenMemory: string | null = null;
  private unseen = 0;
  private baseTitle: string | undefined;

  constructor() {
    effect(() => {
      if (this.auth.isAdmin()) {
        this.start();
      } else {
        this.stop();
      }
    });
  }

  private start(): void {
    if (this.timer !== undefined) {
      return;
    }
    void this.check();
    this.timer = setInterval(() => void this.check(), POLL_MS);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private stop(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  /** Browsers slow timers in background tabs: check as soon as the admin returns. */
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') {
      if (this.baseTitle !== undefined) {
        document.title = this.baseTitle;
        this.baseTitle = undefined;
        this.unseen = 0;
      }
      void this.check();
    }
  };

  private async check(): Promise<void> {
    if (this.checking) {
      return;
    }
    this.checking = true;
    try {
      const latest = await this.orders.latest();
      const seen = this.seenMemory ?? readSeen();
      if (latest.length === 0) {
        // Nothing yet: remember that, so the very first order does alert.
        if (seen === null) {
          this.seenMemory = NO_ORDERS;
          writeSeen(NO_ORDERS);
        }
        return;
      }
      const newest = latest.reduce((a, b) =>
        b.createdAt > a.createdAt ? b : a,
      );
      this.seenMemory = newest.createdAt;
      writeSeen(newest.createdAt);
      if (seen === null) {
        return; // first run on this device: just record where we are
      }
      const fresh = latest
        .filter(order => order.createdAt > seen)
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      if (fresh.length > 0) {
        this.announce(fresh);
      }
    } catch (err) {
      // A network blip or an expired session: try again next time.
      console.warn('Order alert check failed', err);
    } finally {
      this.checking = false;
    }
  }

  private announce(fresh: Order[]): void {
    const message =
      fresh.length === 1
        ? (this.translate.instant('orderAlert.one', {
            number: orderNumber(fresh[0]),
            total: `${fresh[0].currency} ${fresh[0].total.toFixed(2)}`,
          }) as string)
        : (this.translate.instant('orderAlert.many', {
            count: fresh.length,
          }) as string);
    void this.toast.info(message, TOAST_MS);
    this.beep();
    this.flagTab(fresh.length);
  }

  /** While the tab is in the background, put the count in its title so the
   * admin can see it among their tabs; it clears when they come back. */
  private flagTab(count: number): void {
    if (document.visibilityState === 'visible') {
      return;
    }
    this.unseen += count;
    const base = this.baseTitle ?? document.title.replace(/^\(\d+\) /, '');
    this.baseTitle = base;
    document.title = `(${this.unseen}) ${base}`;
  }

  /** A short two-tone beep. Browsers only allow sound after the person has
   * interacted with the page, so a failure here is silently fine. */
  private beep(): void {
    try {
      const Ctx =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctx) {
        return;
      }
      this.audio ??= new Ctx();
      const ctx = this.audio;
      void ctx.resume();
      [880, 1175].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const start = ctx.currentTime + i * 0.18;
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.2, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + 0.18);
      });
    } catch {
      // No audio available.
    }
  }
}

function readSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

function writeSeen(value: string): void {
  try {
    localStorage.setItem(SEEN_KEY, value);
  } catch {
    // Storage blocked: the alert just repeats after a reload.
  }
}
